import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';
import img3 from '../images/web-dev.svg';

const ProjectLeadership = () => {
    useDocTitle('Project Leadership - Claravio Solutions');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                {/* Hero Section */}
                <div className="bg-claravio-blue-900 text-white py-20">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-4">Project Leadership</h1>
                        <p className="text-xl lg:text-2xl mb-8">Execute complex initiatives with proven leadership discipline</p>
                        <Link to="/contact" className="bg-white text-claravio-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Lead Your Project
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <img src={img3} alt="Project Leadership" className="w-full max-w-md mx-auto" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-claravio-blue-900 mb-6">Navigate Complex Initiatives</h2>
                            <p className="text-lg text-gray-700 mb-6">
                                Claravio provides Project Leadership and Execution Discipline to deliver complex and top 
                                priority initiatives. Our Leadership focused approach allows us to navigate past the typical 
                                stumbling blocks encountered when leveraging a pure academic or text book approach.
                            </p>
                            <p className="text-lg text-gray-700">
                                Real-world projects require more than theoretical knowledge. Our experienced leaders bring 
                                practical wisdom, stakeholder management skills, and the flexibility to adapt when plans 
                                meet reality.
                            </p>
                        </div>
                    </div>

                    {/* Leadership Approach */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Our Leadership Approach</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 1L9 9l-8 0 6.5 4.7L5 22l7-5 7 5-2.5-8.3L23 9l-8 0z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Vision-Driven</h3>
                                <p className="text-gray-600">Clear strategic direction with measurable outcomes and stakeholder alignment</p>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Adaptive</h3>
                                <p className="text-gray-600">Flexible methodology that adapts to changing requirements and constraints</p>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 1v6h6L8.5 5.5 10 4h5v2h-3l1 1 3.25 3.25c.31.31.69.5 1.06.69L18 12h4v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h6L8.5 2.5 4 1z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Results-Focused</h3>
                                <p className="text-gray-600">Disciplined execution with continuous monitoring and course correction</p>
                            </div>
                        </div>
                    </div>

                    {/* Services Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Project Leadership Services</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-6">Strategic Program Management</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Multi-project portfolio coordination</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Resource allocation and optimization</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Strategic alignment and governance</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Executive reporting and communication</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-6">Complex Initiative Leadership</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Large-scale transformation projects</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Cross-functional team coordination</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Stakeholder engagement and alignment</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Risk mitigation and issue resolution</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-6">Crisis & Turnaround Management</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Failed project recovery</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Rapid problem diagnosis</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Emergency response planning</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Stakeholder confidence restoration</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-6">Team Development & Mentoring</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Project manager coaching</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Team building and motivation</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Leadership skill development</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Knowledge transfer and documentation</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Differentiators Section */}
                    <div className="bg-white rounded-lg p-8 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Why Choose Claravio Leadership?</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="text-center">
                                <h3 className="font-semibold text-claravio-blue-900 mb-4">Practical Experience</h3>
                                <p className="text-gray-600">Real-world project leadership across industries and organizational sizes</p>
                            </div>
                            <div className="text-center">
                                <h3 className="font-semibold text-claravio-blue-900 mb-4">Proven Methodology</h3>
                                <p className="text-gray-600">Refined approaches that balance structure with flexibility</p>
                            </div>
                            <div className="text-center">
                                <h3 className="font-semibold text-claravio-blue-900 mb-4">Stakeholder Focus</h3>
                                <p className="text-gray-600">Strong emphasis on communication and relationship management</p>
                            </div>
                            <div className="text-center">
                                <h3 className="font-semibold text-claravio-blue-900 mb-4">Delivery Commitment</h3>
                                <p className="text-gray-600">Accountable for outcomes, not just activities</p>
                            </div>
                        </div>
                    </div>

                    {/* Success Metrics 
                    <div className="bg-blue-50 rounded-lg p-8 mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Project Success Metrics</h2>
                        <div className="grid md:grid-cols-3 gap-8 text-center">
                            <div>
                                <div className="text-4xl font-bold text-claravio-blue-900 mb-2">95%</div>
                                <p className="text-gray-600">Projects delivered on time and within budget</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-claravio-blue-900 mb-2">88%</div>
                                <p className="text-gray-600">Stakeholder satisfaction rate</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-claravio-blue-900 mb-2">92%</div>
                                <p className="text-gray-600">Achievement of project objectives</p>
                            </div>
                        </div>
                    </div>
                        */}
                    {/* CTA Section */}
                    <div className="text-center bg-claravio-blue-900 text-white p-12 rounded-lg">
                        <h2 className="text-3xl font-bold mb-4">Ready to Lead Your Next Initiative?</h2>
                        <p className="text-xl mb-8">Let our experienced leaders guide your complex projects to success.</p>
                        <Link to="/contact" className="bg-white text-claravio-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Start Your Project
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ProjectLeadership;